import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  verificationErrorPath,
  verificationPartialSuccessPath,
  verificationSuccessPath
} from 'components/Routes'
import { useIdentityVerification } from 'hooks/useCompliance'

import { Box, Theme, Typography } from '@mui/material'
import DefaultLayout from 'components/layout/DefaultLayout'
import LoadingWrapper from 'components/layout/LoadingWrapper'

import { makeStyles } from 'tss-react/mui'
import { useAuth } from 'components/AuthProvider'
import { useQueryClient } from 'react-query'

const useStyles = makeStyles()((theme: Theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',

    position: 'relative',
    height: '100%',
    marginTop: theme.spacing(4),
    background: theme.palette.error.main
  },
  loading: {
    margin: 'auto',
    color: theme.palette.primary.main
  }
}))

export default function Verifying(): JSX.Element {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const { refreshCurrentUser, setToast } = useAuth()

  // Poll every 500ms while request is in progress
  const { data: statusData, isLoading: statusLoading } =
    useIdentityVerification()

  const requestInProgress = statusData?.status === 'pending'
  const identityVerified = statusData?.verified
  const resolvedRef = useRef(false)

  // React Query polling: refetch on interval while still pending
  useEffect(() => {
    if (!statusData || resolvedRef.current) return

    if (requestInProgress) {
      const interval = setInterval(() => {
        qc.invalidateQueries(['identity-verification'])
      }, 1000)
      return () => clearInterval(interval)
    }

    // Not pending anymore — navigate
    resolvedRef.current = true
    const handleResolved = async () => {
      try {
        const user = await refreshCurrentUser()
        const { onboarded, partialIdentityVerified } =
          (user?.profile as {
            onboarded?: boolean
            partialIdentityVerified?: boolean
          }) || {}

        const isPartialVerified = !identityVerified && partialIdentityVerified
        const isSuccess = identityVerified && onboarded

        if (isPartialVerified) {
          navigate(verificationPartialSuccessPath())
        } else if (isSuccess) {
          navigate(verificationSuccessPath())
        } else {
          navigate(verificationErrorPath())
        }
      } catch (err) {
        setToast({
          open: true,
          message: 'Verification status error',
          type: 'error',
          duration: 3000
        })
        navigate(verificationErrorPath())
      }
    }

    void handleResolved()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData, requestInProgress, identityVerified])

  return (
    <DefaultLayout
      heading='Hold on while we verify your details'
      wrapperContainer={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      <Typography variant='body1' textAlign={'center'}>
        (This may take 5-10 seconds)
      </Typography>
      <Box className={classes.wrapper}>
        <LoadingWrapper
          className={classes.loading}
          loading={statusLoading || requestInProgress}
        >
          {/* This is a hack as LoadingWrapper requires `children` */}
          <div />
        </LoadingWrapper>
      </Box>
    </DefaultLayout>
  )
}
